import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError, of } from 'rxjs';
import { environment } from 'src/environments/environment';
import { SparePartModel } from 'src/app/models/spare-part-model/spare-part-model';
import { readNeedReviewFlag, normalizeIsDamaged } from 'src/app/utils/spare-part-packaging.util';
import { stripPascalCaseShadowKeys, toCanonicalSparePartKeys } from 'src/app/utils/spare-part-payload.util';
import { parseWallClockDateTimeFromApi } from 'src/app/utils/wall-clock-datetime.util';
import { catchError, map, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class SparePartsService {

  private apiUrl = environment.apiEndpoint + '/SparePart';

  constructor(
    private http: HttpClient
  ) { }
  
  addSparePart(sparePart: SparePartModel, orderId: number): Observable<any> {
    const body = stripPascalCaseShadowKeys({
      ...sparePart,
      needReview: readNeedReviewFlag(sparePart as any),
      isDamaged: normalizeIsDamaged(sparePart.isDamaged ?? (sparePart as any).IsDamaged)
    });
    return this.http.post<any>(`${this.apiUrl}/${orderId}`, body);
  }

  deleteSparePart(sparePartId: number, orderId: number): Observable<string> {
    return this.http.delete<string>(`${this.apiUrl}/${sparePartId}/${orderId}`);
  }

  getAllSpareParts(): Observable<any> {
    try {
      return this.http.get<any>(`${this.apiUrl}/allSpareParts`);
    }
    catch (error) {
      return throwError(error);
    }
  }

  // Método para obtener el conteo total de repuestos (opcional: solo últimos maxAgeDays)
  getTotalSparePartsCount(maxAgeDays?: number | null, lowSaleRatioOnly: boolean = false): Observable<number> {
    let params = new HttpParams();
    if (maxAgeDays != null && maxAgeDays > 0) {
      params = params.set('maxAgeDays', maxAgeDays.toString());
    }
    if (lowSaleRatioOnly) {
      params = params.set('lowSaleRatioOnly', 'true');
    }
    const options = params.keys().length > 0 ? { params } : {};
    return this.http.get<number>(`${this.apiUrl}/count`, options);
  }

  downloadStatisticsExcel(
    searchTerm?: string,
    sortBy?: string,
    sortOrder?: 'asc' | 'desc',
    columnFilters?: Record<string, string[]>,
    maxAgeDays?: number | null,
    lowSaleRatioOnly: boolean = false
  ): Observable<Blob> {
    let params = new HttpParams();
    if (searchTerm?.trim()) {
      params = params.set('searchTerm', searchTerm.trim());
    }
    if (sortBy) {
      params = params.set('sortBy', sortBy);
    }
    if (sortOrder) {
      params = params.set('sortOrder', sortOrder);
    }
    if (columnFilters && Object.keys(columnFilters).length > 0) {
      params = params.set('columnFilters', JSON.stringify(columnFilters));
    }
    if (maxAgeDays != null && maxAgeDays > 0) {
      params = params.set('maxAgeDays', maxAgeDays.toString());
    }
    if (lowSaleRatioOnly) {
      params = params.set('lowSaleRatioOnly', 'true');
    }

    return this.http.get(`${environment.apiEndpoint}/api/exports/repuestos/manual`, {
      params,
      responseType: 'blob'
    });
  }

  getDistinctColumnValues(
    column: string,
    searchTerm?: string,
    startDate?: Date,
    endDate?: Date,
    productStatus?: string,
    shippingStatus?: string,
    receptionStatus?: string,
    columnFilters?: Record<string, string[]>,
    maxAgeDays?: number | null,
    lowSaleRatioOnly: boolean = false
  ): Observable<{ values: string[]; hasEmpty: boolean }> {
    let params = new HttpParams().set('column', column);

    if (searchTerm) {
      params = params.set('searchTerm', searchTerm);
    }
    if (startDate) {
      params = params.set('startDate', startDate.toISOString());
    }
    if (endDate) {
      params = params.set('endDate', endDate.toISOString());
    }
    if (productStatus) {
      params = params.set('productStatus', productStatus);
    }
    if (shippingStatus) {
      params = params.set('shippingStatus', shippingStatus);
    }
    if (receptionStatus) {
      params = params.set('receptionStatus', receptionStatus);
    }
    if (columnFilters && Object.keys(columnFilters).length > 0) {
      params = params.set('columnFilters', JSON.stringify(columnFilters));
    }
    if (maxAgeDays != null && maxAgeDays > 0) {
      params = params.set('maxAgeDays', maxAgeDays.toString());
    }
    if (lowSaleRatioOnly) {
      params = params.set('lowSaleRatioOnly', 'true');
    }

    return this.http.get<{ values: string[]; hasEmpty: boolean }>(`${this.apiUrl}/distinct-values`, { params }).pipe(
      map(response => ({
        values: Array.isArray(response?.values) ? response.values : [],
        hasEmpty: !!response?.hasEmpty
      })),
      catchError(error => {
        console.error('Error en getDistinctColumnValues:', error);
        return of({ values: [], hasEmpty: false });
      })
    );
  }

  // Método para obtener repuestos paginados (filtros aplicados en backend a todo el conjunto)
  getPaginatedSpareParts(
    page: number = 1, 
    pageSize: number = 100, 
    searchTerm?: string,
    startDate?: Date,
    endDate?: Date,
    productStatus?: string,
    shippingStatus?: string,
    receptionStatus?: string,
    sortBy?: string,
    sortOrder?: 'asc' | 'desc',
    columnFilters?: Record<string, string[]>,
    maxAgeDays?: number | null,
    lowSaleRatioOnly: boolean = false
  ): Observable<{
    spareParts: SparePartModel[],
    totalCount: number,
    totalCost?: number,
    totalSalesValue?: number,
    page: number,
    pageSize: number,
    totalPages: number
  }> {
    try {
      let params = new HttpParams()
        .set('page', page.toString())
        .set('pageSize', pageSize.toString())
        .set('includeOrderNumber', 'true');
      
      if (searchTerm) {
        params = params.set('searchTerm', searchTerm);
      }
      
      if (startDate) {
        params = params.set('startDate', startDate.toISOString());
      }
      
      if (endDate) {
        params = params.set('endDate', endDate.toISOString());
      }
      if (productStatus) {
        params = params.set('productStatus', productStatus);
      }
      if (shippingStatus) {
        params = params.set('shippingStatus', shippingStatus);
      }
      if (receptionStatus) {
        params = params.set('receptionStatus', receptionStatus);
      }
      if (sortBy) {
        params = params.set('sortBy', sortBy);
      }
      if (sortOrder) {
        params = params.set('sortOrder', sortOrder);
      }
      if (columnFilters && Object.keys(columnFilters).length > 0) {
        params = params.set('columnFilters', JSON.stringify(columnFilters));
      }
      if (maxAgeDays != null && maxAgeDays > 0) {
        params = params.set('maxAgeDays', maxAgeDays.toString());
      }
      if (lowSaleRatioOnly) {
        params = params.set('lowSaleRatioOnly', 'true');
      }
      
      return this.http.get<any>(`${this.apiUrl}/paginated`, { params }).pipe(
        map(response => {
          // Asegurarse de que response y response.spareParts existan
          if (!response) {
            console.error('La respuesta del servidor es null o undefined');
            return {
              spareParts: [],
              totalCount: 0,
              page: page,
              pageSize: pageSize,
              totalPages: 0
            };
          }
          
          if (!response.spareParts) {
            console.error('La respuesta del servidor no contiene la propiedad spareParts:', response);
            response.spareParts = [];
          }
          
          const hasDateValue = (value: unknown): boolean => value != null && value !== '';

          const mappedSpareParts = (response.spareParts || []).map((sp: any) => {
            const canonical = toCanonicalSparePartKeys(sp);
            const rawExpected = canonical.expectedArrivalDate;
            const expectedArrivalParsed = hasDateValue(rawExpected)
              ? new Date(rawExpected as string | Date)
              : undefined;

            return {
              ...canonical,
              orderDate: canonical.orderDate ? new Date(canonical.orderDate as string | Date) : undefined,
              orderExpectedArrivalDate: canonical.orderExpectedArrivalDate
                ? new Date(canonical.orderExpectedArrivalDate as string | Date)
                : undefined,
              ...(expectedArrivalParsed != null && !isNaN(expectedArrivalParsed.getTime())
                ? { expectedArrivalDate: expectedArrivalParsed }
                : {}),
              arrivalDate: hasDateValue(canonical.arrivalDate)
                ? parseWallClockDateTimeFromApi(canonical.arrivalDate as string | Date)
                : undefined,
              lastExportedAt: hasDateValue(canonical.lastExportedAt)
                ? new Date(canonical.lastExportedAt as string | Date)
                : undefined,
              fechaExportacion: hasDateValue(canonical.fechaExportacion)
                ? new Date(canonical.fechaExportacion as string | Date)
                : undefined,
              fechaDeComunicado: hasDateValue(canonical.fechaDeComunicado)
                ? new Date(canonical.fechaDeComunicado as string | Date)
                : undefined,
              mlDeadline: hasDateValue(canonical.mlDeadline)
                ? new Date(canonical.mlDeadline as string | Date)
                : undefined,
              siniestro: canonical.siniestro || sp.siniestro || sp.Siniestro || '',
              vendedor: canonical.vendedor || sp.vendedor || sp.Vendedor || sp.employeeName || '',
              fornecedor: canonical.fornecedor || sp.fornecedor || sp.Fornecedor || sp.providerName || '',
              clientName: canonical.clientName || sp.clientName || sp.ClientName || '',
              clientPhone: canonical.clientPhone || sp.clientPhone || sp.ClientPhone || '',
              invoiceNumber: canonical.invoiceNumber || sp.invoiceNumber || sp.InvoiceNumber || '',
              providerPhone: canonical.providerPhone || sp.providerPhone || sp.ProviderPhone || '',
              orderStatus: canonical.orderStatus || sp.orderStatus || sp.OrderStatus || '',
              carMakeModel: canonical.carMakeModel || sp.carMakeModel || sp.CarMakeModel || '',
              deliveryAddress: canonical.deliveryAddress || sp.deliveryAddress || sp.DeliveryAddress || '',
              isExport: canonical.isExport || sp.isExport || sp.IsExport || '',
              sendYesOrNo: canonical.sendYesOrNo || sp.sendYesOrNo || sp.SendYesOrNo || '',
              workshopName: canonical.workshopName || sp.workshopName || sp.WorkshopName || '',
              workshopCity: canonical.workshopCity || sp.workshopCity || sp.WorkshopCity || '',
              workshopDepartment: canonical.workshopDepartment || sp.workshopDepartment || sp.WorkshopDepartment || '',
              orderSparePartsCount: canonical.orderSparePartsCount ?? sp.orderSparePartsCount ?? sp.OrderSparePartsCount ?? 0,
              orderTotalPrice: canonical.orderTotalPrice ?? sp.orderTotalPrice ?? sp.OrderTotalPrice ?? 0
            };
          });
          
          return {
            spareParts: mappedSpareParts,
            totalCount: response.totalCount || 0,
            totalCost: typeof response.totalCost === 'number' ? response.totalCost : undefined,
            totalSalesValue: typeof response.totalSalesValue === 'number' ? response.totalSalesValue : undefined,
            page: response.page || page,
            pageSize: response.pageSize || pageSize,
            totalPages: response.totalPages || 0
          };
        }),
        catchError(error => {
          console.error('Error al obtener repuestos paginados:', error);
          return of({
            spareParts: [],
            totalCount: 0,
            totalCost: 0,
            totalSalesValue: 0,
            page: page,
            pageSize: pageSize,
            totalPages: 0
          });
        })
      );
    } catch (error) {
      console.error('Error al obtener repuestos paginados:', error);
      return of({
        spareParts: [],
        totalCount: 0,
        totalCost: 0,
        totalSalesValue: 0,
        page: page,
        pageSize: pageSize,
        totalPages: 0
      });
    }
  }
  
  // Método auxiliar para obtener órdenes para repuestos
  private getOrdersForSpareParts(spareParts: SparePartModel[]): Observable<SparePartModel[]> {
    if (!spareParts || spareParts.length === 0) {
      return of([]);
    }
    
    const sparePartIds = spareParts.map(sp => sp.sparePartId);
    
    return this.http.post<any>(`${this.apiUrl}/getOrderNumbers`, { sparePartIds }).pipe(
      map(response => {
        return spareParts.map(sparePart => {
          const orderInfo = response.find((r: any) => r.sparePartId === sparePart.sparePartId);
          return {
            ...sparePart,
            orderNumber: orderInfo ? orderInfo.orderNumber : 'N/A'
          };
        });
      }),
      catchError(error => {
        console.error('Error al obtener números de orden:', error);
        return of(spareParts); 
      })
    );
  }

  updateSparePart(sparePart: SparePartModel, orderId: number): Observable<any> {
    try {
      const url = `${this.apiUrl}/update-spare-part?orderId=${orderId}`;
      
      const body = stripPascalCaseShadowKeys({
        ...toCanonicalSparePartKeys(sparePart as Record<string, unknown>),
        needReview: readNeedReviewFlag(sparePart as any),
        isDamaged: normalizeIsDamaged(sparePart.isDamaged ?? (sparePart as any).IsDamaged)
      });
      return this.http.put<any>(url, body).pipe(
        catchError(error => {
          console.error('❌ Error en updateSparePart:', error);
          return throwError(() => error);
        })
      );
    }
    catch (error) {
      return throwError(() => error);
    }
  }

  updateSpareParts(sparePart: SparePartModel[]): Observable<any> {
    try {
      const body = sparePart.map(part => stripPascalCaseShadowKeys(part));
      return this.http.put<any>(`${this.apiUrl}/updateSpareParts`, body);
    }
    catch (error) {
      return throwError(error);
    }
  }

  applySendYesOrNoToOrderSpareParts(orderId: number, sendYesOrNo: 'true' | 'false'): Observable<{ updatedCount: number }> {
    return this.http.put<{ updatedCount: number }>(
      `${this.apiUrl}/order/${orderId}/send-yes-or-no`,
      { sendYesOrNo }
    );
  }

  // Método para actualizar todos los repuestos filtrados
  updateAllFilteredSparePartsStatus(
    newStatus: string,
    searchTerm: string = '',
    startDate?: Date,
    endDate?: Date
  ): Observable<any> {
    // Construir la URL base
    let url = `${environment.apiEndpoint}/SparePart/UpdateAllFilteredStatus`;
    
    // Agregar los parámetros de consulta
    const params = new HttpParams()
      .set('newStatus', newStatus)
      .set('searchTerm', searchTerm || '');
    
    // Agregar parámetros de fecha si están presentes
    let paramsWithDates = params;
    if (startDate) {
      paramsWithDates = paramsWithDates.set('startDate', startDate.toISOString());
    }
    if (endDate) {
      paramsWithDates = paramsWithDates.set('endDate', endDate.toISOString());
    }
    
    // Hacer la petición PUT con los parámetros
    return this.http.put(url, null, { params: paramsWithDates });
  }

  assignExportInfo(exportNumber: string, exportDate: string): Observable<any> {
    const payload = {
      exportNumber,
      exportDate
    };

    return this.http.put<any>(`${this.apiUrl}/assign-export-info`, payload).pipe(
      catchError(error => {
        console.error('Error al asignar información de exportación:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Actualización masiva parcial: peso líquido (netWheight), peso bruto (grossWheight), país.
   * Solo enviar propiedades a modificar por repuesto.
   */
  bulkUpdateSparePartsPhysical(
    updates: Array<{ sparePartId: number; netWheight?: number; grossWheight?: number; country?: string }>
  ): Observable<{ updatedCount: number }> {
    return this.http.put<{ updatedCount: number }>(`${this.apiUrl}/bulkUpdatePhysical`, { updates }).pipe(
      catchError(error => {
        console.error('Error en bulkUpdateSparePartsPhysical:', error);
        return throwError(() => error);
      })
    );
  }

  bulkUpdateSparePartShippingReceipts(
    updates: Array<{ sparePartId: number; shippingReceipt: string }>
  ): Observable<{ updatedCount: number; message: string }> {
    return this.http.put<{ updatedCount: number; message: string }>(
      `${this.apiUrl}/bulkUpdateShippingReceipts`,
      { updates }
    ).pipe(
      catchError(error => {
        console.error('Error en bulkUpdateSparePartShippingReceipts:', error);
        return throwError(() => error);
      })
    );
  }

  // Método para actualizar el número de paquete de un repuesto específico
  updateSparePartPackageNumber(sparePartId: number, packageNumber: string): Observable<any> {
    try {
      return this.http.put<any>(`${this.apiUrl}/updatePackageNumber/${sparePartId}`, JSON.stringify(packageNumber), {
        headers: {
          'Content-Type': 'application/json'
        }
      }).pipe(
        catchError(error => {
          console.error('Error al actualizar número de paquete:', error);
          return throwError(() => error);
        })
      );
    } catch (error) {
      console.error('Error al actualizar número de paquete:', error);
      return throwError(() => error);
    }
  }

  // Método para actualizar los números de paquete de múltiples repuestos
  updateSparePartsPackageNumbers(packageUpdates: { [sparePartId: number]: string }): Observable<any> {
    // Envolver el diccionario en la estructura esperada por el backend
    const request = {
      PackageUpdates: packageUpdates
    };
    return this.http.put<any>(`${this.apiUrl}/updatePackageNumbers`, request).pipe(
      catchError(error => {
        console.error('Error del backend:', error);
        console.error('Status:', error.status);
        console.error('Error body:', error.error);
        return throwError(() => error);
      })
    );
  }

  // Método para buscar repuestos por código (para gestión de paquetes)
  findSparePartByCode(code: string): Observable<any[]> {
    const params = new HttpParams().set('code', code);
    return this.http.get<any[]>(`${this.apiUrl}/search`, { params });
  }

  // Método para actualizar el shippingStatus de un repuesto específico
  updateSparePartShippingStatus(sparePartId: number, shippingStatus: string): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/${sparePartId}/shipping-status`, { shippingStatus }).pipe(
      catchError(error => {
        console.error(`Error actualizando shipping status para repuesto ${sparePartId}:`, error);
        console.error('Detalles del error:', {
          status: error.status,
          statusText: error.statusText,
          error: error.error,
          message: error.message
        });
        return throwError(() => error);
      })
    );
  }

  // Método para actualizar el shippingReceipt de un repuesto específico
  updateSparePartShippingReceipt(sparePartId: number, shippingReceipt: string): Observable<any> {
    // CRÍTICO: Asegurar que el string no esté vacío
    if (!shippingReceipt || shippingReceipt.trim() === '') {
      console.error(`❌ FRONTEND - ERROR: shippingReceipt está vacío o null!`);
      return throwError(() => new Error('El remito no puede estar vacío'));
    }
    
    const trimmedReceipt = shippingReceipt.trim();
    
    // IMPORTANTE: Envolver el string en JSON.stringify para que el backend lo reciba correctamente
    const body = JSON.stringify(trimmedReceipt);
    
    const headers = { 'Content-Type': 'application/json' };
    
    return this.http.put<any>(`${this.apiUrl}/updateShippingReceipt/${sparePartId}`, body, { headers }).pipe(
      catchError(error => {
        console.error(`❌ FRONTEND - Error actualizando shipping receipt para repuesto ${sparePartId}:`, error);
        console.error('❌ FRONTEND - Detalles del error:', {
          status: error.status,
          statusText: error.statusText,
          error: error.error,
          message: error.message,
          shippingReceiptEnviado: trimmedReceipt,
          url: `${this.apiUrl}/updateShippingReceipt/${sparePartId}`,
          bodyEnviado: body
        });
        return throwError(() => error);
      })
    );
  }

  // Método para actualizar solo el flag FANT de un repuesto
  updateSparePartFantFlag(sparePartId: number, isFant: boolean): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/${sparePartId}/fant`, {
      isFant
    });
  }

  updateSparePartFantUrgency(sparePartId: number, fantUrgency: 'green' | 'yellow' | 'red' | null): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/${sparePartId}/fant-urgency`, {
      fantUrgency
    });
  }

  // Método para actualizar solo el flag Recompra de un repuesto
  updateSparePartRecompraFlag(sparePartId: number, recompra: boolean): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/${sparePartId}/recompra`, {
      recompra
    });
  }

  // Método para actualizar solo el flag Control de un repuesto
  updateSparePartControlFlag(sparePartId: number, control: boolean): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/${sparePartId}/control`, {
      control
    });
  }

  updateSparePartAnuladoFlag(sparePartId: number, anulado: boolean): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/${sparePartId}/anulado`, {
      anulado
    });
  }

  updateSparePartTrelloFlag(sparePartId: number, trello: boolean): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/${sparePartId}/trello`, {
      trello
    });
  }

  updateSparePartDevZureoFlag(sparePartId: number, devZureo: boolean): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/${sparePartId}/devzureo`, {
      devZureo
    });
  }

  updateSparePartEntradaSageFlag(sparePartId: number, entradaSage: boolean): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/${sparePartId}/entradasage`, {
      entradaSage
    });
  }

  updateSparePartControleSebaFlag(sparePartId: number, controleSeba: boolean): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/${sparePartId}/controleseba`, {
      controleSeba
    });
  }

  updateSparePartOkAnulacaoFlag(sparePartId: number, okAnulacao: boolean): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/${sparePartId}/okanulacao`, {
      okAnulacao
    });
  }

  updateSparePartFacturadoFlag(sparePartId: number, facturado: boolean): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/${sparePartId}/facturado`, {
      facturado
    });
  }

  // Método para actualizar el ProductStatus de un repuesto específico
  updateSparePartStatus(sparePartId: number, productStatus: string): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/${sparePartId}/status`, { productStatus }).pipe(
      catchError(error => {
        console.error(`Error actualizando product status para repuesto ${sparePartId}:`, error);
        console.error('Detalles del error:', {
          status: error.status,
          statusText: error.statusText,
          error: error.error,
          message: error.message
        });
        return throwError(() => error);
      })
    );
  }
}
